import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, ArrowLeft, CalendarClock, FileSpreadsheet, Loader2, Monitor, Send } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { fetchRaportySps, makroDaty, opisWyzwalacza, type RaportSpsWidok, type UruchomienieSps } from '@/lib/raporty-sps';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  RAPORTY SKONFIGUROWANE W SPS (13.10.2026, user: „w zakładce raporty wyświetlać to, co jest
 *  skonfigurowane w SPS, i co jest w harmonogramie zadań z jakimi parametrami — tak by na tej
 *  podstawie można było przygotować raporty u nas").
 *
 *  ⚠️ TYLKO PODGLĄD. Nic stąd nie zmienia SPS-a ani naszych harmonogramów — wysyłka powstaje osobnym
 *  torem. Dane przysyła agent raz na godzinę (przy zmianie), więc ekran pokazuje porę zgłoszenia.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function RaportySps() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const q = useQuery({
    queryKey: ['raporty-sps', groupId],
    queryFn: () => fetchRaportySps(groupId!),
    enabled: groupId !== null,
  });
  const d = q.data;
  /**
   * TYLKO WŁĄCZONE (13.10.2026, user) — raporty, które Harmonogram naprawdę uruchamia, i w nich tylko
   * włączone zadania. Domyślnie wszystko: wyłączone zadanie też mówi, jak raport był wysyłany.
   */
  const [tylkoWlaczone, setTylkoWlaczone] = useState(false);
  const raporty = (d?.raporty ?? [])
    .map((r) => (tylkoWlaczone ? { ...r, uruchomienia: r.uruchomienia.filter((u) => u.wlaczone) } : r))
    .filter((r) => !tylkoWlaczone || r.uruchomienia.length > 0);
  const bezRaportu = (d?.zadaniaBezRaportu ?? []).filter((u) => !tylkoWlaczone || u.wlaczone);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <FileSpreadsheet className="size-6" />
            {t('rsps.tytul')}
          </h1>
          {d?.zrodlo && (
            <p className="mt-1 text-sm text-muted-foreground">
              {t('rsps.zrodlo')
                .replace('{maszyna}', d.zrodlo.maszyna ?? d.zrodlo.wezel)
                .replace('{kiedy}', new Date(d.zrodlo.zgloszonoO).toLocaleString('pl'))}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={tylkoWlaczone ? 'default' : 'outline'}
            size="sm"
            aria-pressed={tylkoWlaczone}
            onClick={() => setTylkoWlaczone((v) => !v)}
          >
            {t('rsps.tylkoWlaczone')}
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/raportowanie">
              <ArrowLeft className="size-4" />
              {t('harm.wrocDoRaportow')}
            </Link>
          </Button>
        </div>
      </div>

      {q.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
        </p>
      ) : !d || (d.raporty.length === 0 && d.zadaniaBezRaportu.length === 0) ? (
        <p className="rounded-md border border-border p-4 text-sm text-muted-foreground">{t('rsps.brak')}</p>
      ) : raporty.length === 0 && bezRaportu.length === 0 ? (
        <p className="rounded-md border border-border p-4 text-sm text-muted-foreground">{t('rsps.brakWlaczonych')}</p>
      ) : (
        <>
          {raporty.map((r) => (
            <KartaRaportu key={r.nazwa} r={r} />
          ))}
          {bezRaportu.length > 0 && (
            <section className="space-y-2 rounded-lg border border-[var(--warning)]/40 p-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <AlertTriangle className="size-4 text-[var(--warning)]" />
                {t('rsps.bezRaportu')}
              </h2>
              <Uruchomienia lista={bezRaportu} pokazRaport />
            </section>
          )}
        </>
      )}

      {d && d.wezly.some((w) => w.bledy.length > 0) && (
        <section className="space-y-1 text-xs text-muted-foreground">
          {d.wezly
            .filter((w) => w.bledy.length > 0)
            .map((w) => (
              <p key={w.wezel}>
                {w.maszyna ?? w.wezel}: {w.bledy.join('; ')}
              </p>
            ))}
        </section>
      )}
    </div>
  );
}

function KartaRaportu({ r }: { r: RaportSpsWidok }) {
  const { t } = useI18n();
  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-base font-semibold">{r.nazwa}</h2>
        <span className="text-sm text-muted-foreground">
          {r.bazowy.nazwa ?? r.bazowy.id} <span className="font-mono text-xs">({r.bazowy.id})</span>
        </span>
        {r.formatPliku && <Badge variant="neutral">{r.formatPliku}</Badge>}
        {r.zbiorczy && <Badge variant="neutral">{t('rsps.zbiorczy')}</Badge>}
        {r.tylkoSumy && <Badge variant="neutral">{t('rsps.tylkoSumy')}</Badge>}
        {r.dataWNazwie && <Badge variant="neutral">{t('rsps.dataWNazwie')}</Badge>}
        {r.topN && <Badge variant="neutral">{t('rsps.topN').replace('{n}', String(r.topN.ile ?? '?')).replace('{pole}', r.topN.pole ?? '?')}</Badge>}
      </div>
      {r.skonfigurowanyNa.length > 0 && (
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <Monitor className="size-3.5" />
          {t('rsps.skonfigurowanyNa')}:
          {r.skonfigurowanyNa.map((m) => (
            <Badge
              key={m.wezel}
              variant={m.takSamo ? 'neutral' : 'warning'}
              title={m.takSamo ? undefined : t('rsps.innaWersja')}
            >
              {m.maszyna ?? m.wezel}
            </Badge>
          ))}
        </p>
      )}
      {r.naglowek && <p className="text-xs text-muted-foreground">{t('rsps.naglowek')}: {r.naglowek}</p>}

      <div className="grid gap-3 lg:grid-cols-2">
        <div>
          <p className="text-xs font-medium">{t('rsps.kolumny')}</p>
          <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-xs">
            {r.kolumny.map((k) => (
              <li key={k.pole}>
                {k.podpis ?? k.pole} <span className="font-mono text-muted-foreground">{k.pole}</span>
                {k.wyrazenie && <span className="font-mono text-muted-foreground"> = {k.wyrazenie}</span>}
                {k.grupuj && <Badge variant="neutral" className="ml-1">{t('rsps.grupuj')}</Badge>}
                {k.suma && <Badge variant="neutral" className="ml-1">{t('rsps.suma')}</Badge>}
              </li>
            ))}
          </ol>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium">{t('rsps.warunki')}</p>
            {r.warunki.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('rsps.brakWarunkow')}</p>
            ) : (
              <ul className="mt-1 space-y-0.5 text-xs">
                {r.warunki.map((w) => (
                  <li key={w.pole}>
                    {w.podpis ?? w.pole}:{' '}
                    {w.wartosc && <span className="font-mono">{makroDaty(w.wartosc, t)}</span>}
                    {(w.od || w.do) && (
                      <span>
                        {t('rsps.od')} <span className="font-mono">{makroDaty(w.od ?? '—', t)}</span> {t('rsps.do')}{' '}
                        <span className="font-mono">{makroDaty(w.do ?? '—', t)}</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="flex items-center gap-1 text-xs font-medium">
              <Send className="size-3.5" />
              {t('rsps.wysylka')}
            </p>
            {r.wysylka.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('rsps.brakWysylki')}</p>
            ) : (
              <ul className="mt-1 space-y-0.5 text-xs">
                {r.wysylka.map((w) => (
                  <li key={w.szablon}>
                    <span className="font-medium">{w.szablon}</span>
                    {w.format && <span className="text-muted-foreground"> ({w.format})</span>}
                    {!w.znany ? (
                      <span className="text-[var(--warning)]"> — {t('rsps.szablonNieznany')}</span>
                    ) : (
                      <span className="text-muted-foreground">
                        {' '}
                        — {w.rodzaj ?? '?'}
                        {w.do && `: ${w.do}`}
                        {w.dw && ` (${t('rsps.dw')} ${w.dw})`}
                        {w.temat && `, „${w.temat}"`}
                        {w.katalog && `, ${w.katalog}`}
                        {w.ftp && `, FTP ${w.ftp}`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div>
        <p className="flex items-center gap-1 text-xs font-medium">
          <CalendarClock className="size-3.5" />
          {t('rsps.harmonogram')}
        </p>
        {r.uruchomienia.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('rsps.brakZadan')}</p>
        ) : (
          <Uruchomienia lista={r.uruchomienia} />
        )}
      </div>
    </section>
  );
}

function Uruchomienia({ lista, pokazRaport = false }: { lista: UruchomienieSps[]; pokazRaport?: boolean }) {
  const { t } = useI18n();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-muted-foreground">
          <tr>
            <th className="py-1 pr-3 font-medium">{t('rsps.maszyna')}</th>
            <th className="py-1 pr-3 font-medium">{t('rsps.zadanie')}</th>
            <th className="py-1 pr-3 font-medium">{t('rsps.kiedy')}</th>
            <th className="py-1 pr-3 font-medium">{t('rsps.parametry')}</th>
            <th className="py-1 pr-3 font-medium">{t('rsps.ostatnio')}</th>
            <th className="py-1 font-medium">{t('rsps.nastepne')}</th>
          </tr>
        </thead>
        <tbody>
          {lista.map((u) => (
            <tr key={`${u.wezel}|${u.sciezka ?? ''}${u.zadanie}`} className="border-t border-border/60 align-top">
              <td className="py-1 pr-3">{u.maszyna ?? u.wezel}</td>
              <td className="py-1 pr-3">
                {u.zadanie}{' '}
                <Badge variant={u.wlaczone ? 'success' : 'neutral'}>{u.wlaczone ? t('rsps.wlaczone') : t('rsps.wylaczone')}</Badge>
                {u.konto && <div className="text-muted-foreground">{t('rsps.konto')}: {u.konto}</div>}
              </td>
              <td className="py-1 pr-3">
                {u.wyzwalacze.length === 0 ? '—' : u.wyzwalacze.map((w, i) => <div key={i}>{opisWyzwalacza(w, t)}</div>)}
              </td>
              <td className="py-1 pr-3 font-mono">
                {u.tryb ?? '?'}
                {pokazRaport && u.raporty.length > 0 && ` ${u.raporty.join(', ')}`}
                {u.parametry.length > 0 && <div className="text-muted-foreground">{u.parametry.join(' | ')}</div>}
              </td>
              <td className="py-1 pr-3">
                {u.ostatnio ? new Date(u.ostatnio).toLocaleString('pl') : '—'}
                {u.wynik !== null && u.wynik !== undefined && u.ostatnio && (
                  <span className={u.wynik === 0 ? 'text-muted-foreground' : 'text-destructive'}> ({u.wynik === 0 ? t('rsps.ok') : `0x${(u.wynik >>> 0).toString(16)}`})</span>
                )}
              </td>
              <td className="py-1">{u.wlaczone && u.nastepne ? new Date(u.nastepne).toLocaleString('pl') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
